"use client";

/**
 * STORY-NEW-043: Refatorado para usar componentes de layout padronizados
 */

import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { useGetUnifiedCampaignByCodeQuery } from "@/services/campaign";
import { useUpdatePro014CampaignMutation } from "@/services/campaign-pro014";
import { useToast } from "components/ui/use-toast";
import { format } from "date-fns";
import { Tag } from "lucide-react";
import { getTranslatedErrorMessage } from "../../utils/error-messages";
import { MESSAGES, NO_CHANGES_TOAST } from "../../utils/messages";
import {
  CampaignConflictModal,
  type CampaignConflictItem,
} from "../campaign-conflict-modal";
import {
  CampaignEditModalLayout,
  ModalLoadingState,
  ModalErrorState,
  ModalFieldGroup,
} from "./modal-layout";
import { PRO014_DESCRIPTION_MAX_LENGTH } from "../../create/constants";
import { PRO014_STORES_TEXTS } from "./constants";
import { Pro014StoresEditor } from "./pro014-stores-editor";

interface EditPro014ModalProps {
  campaignCode: number;
}

const discountTypes = [
  { value: 0, label: "Desconto integral na 2ª peça (mesmo item)", group: "MESMO ITEM" },
  { value: 1, label: "Percentual na 2ª peça (mesmo item)", group: "MESMO ITEM" },
  { value: 2, label: "Valor fixo de desconto (mesmo item)", group: "MESMO ITEM" },
  { value: 3, label: "Desconto integral (itens da lista)", group: "ITENS DA LISTA" },
  { value: 4, label: "Percentual (itens da lista)", group: "ITENS DA LISTA" },
  { value: 5, label: "Valor fixo de desconto (itens da lista)", group: "ITENS DA LISTA" },
  { value: 7, label: "Brinde - escolher item da lista", group: "BRINDE" },
  { value: 8, label: "Brinde - mesmo item", group: "BRINDE" },
];

export function EditPro014Modal({ campaignCode }: EditPro014ModalProps) {
  const { toast } = useToast();
  const { handleModal } = useDialogModal();

  const { data: campaign, isLoading, error } = useGetUnifiedCampaignByCodeQuery(
    `PRO014-${campaignCode}`
  );

  const [updateCampaign, { isLoading: isUpdating }] = useUpdatePro014CampaignMutation();

  // Form state
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [takeQuantity, setTakeQuantity] = useState(2);
  const [payQuantity, setPayQuantity] = useState(1);
  const [discountValue, setDiscountValue] = useState(0);
  const [discountType, setDiscountType] = useState(0);
  const [baseValue, setBaseValue] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [conflicts, setConflicts] = useState<CampaignConflictItem[]>([]);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [hasPendingStores, setHasPendingStores] = useState<boolean>(false);

  // Load campaign data
  useEffect(() => {
    if (campaign) {
      setDescription(campaign.description || "");
      setStartDate(campaign.startDate ? format(new Date(campaign.startDate), "yyyy-MM-dd") : "");
      setEndDate(campaign.endDate ? format(new Date(campaign.endDate), "yyyy-MM-dd") : "");
      setTakeQuantity(campaign.takeQuantity ?? 2);
      setPayQuantity(campaign.payQuantity ?? 1);
      setDiscountValue(campaign.discountValue ?? 0);
      setDiscountType(campaign.discountType ?? 0);
      setBaseValue(campaign.baseValue ?? 0);
    }
  }, [campaign]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!description || description.trim().length < 3) {
      newErrors.description = "Descrição deve ter no mínimo 3 caracteres";
    }
    if (description.length > PRO014_DESCRIPTION_MAX_LENGTH) {
      newErrors.description = `Descrição deve ter no máximo ${PRO014_DESCRIPTION_MAX_LENGTH} caracteres`;
    }
    if (!startDate) {
      newErrors.startDate = "Data de início é obrigatória";
    }
    if (!endDate) {
      newErrors.endDate = "Data de término é obrigatória";
    }
    if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
      newErrors.endDate = "Data de término deve ser maior ou igual à data de início";
    }
    if (takeQuantity < 1) {
      newErrors.takeQuantity = "Quantidade 'Leve' deve ser no mínimo 1";
    }
    if (payQuantity >= takeQuantity) {
      newErrors.payQuantity = "Quantidade 'Pague' deve ser menor que 'Leve'";
    }
    if (discountValue < 0) {
      newErrors.discountValue = "Valor/Percentual não pode ser negativo";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      // Diff payload — só envia campos que mudaram vs valor original.
      // Backend rejeita campos imutáveis em ACTIVE mesmo se o valor for igual,
      // então enviar tudo sempre causaria erro. Snapshot vem do `campaign`
      // hidratado pela query.
      const originalStartDate = campaign?.startDate
        ? format(new Date(campaign.startDate), "yyyy-MM-dd")
        : "";
      const originalEndDate = campaign?.endDate
        ? format(new Date(campaign.endDate), "yyyy-MM-dd")
        : "";

      const updateData: {
        code: number;
        description?: string;
        startDate?: string;
        endDate?: string;
        takeQuantity?: number;
        payQuantity?: number;
        discountValue?: number;
        discountType?: number;
        baseValue?: number;
      } = { code: campaignCode };

      if (description !== (campaign?.description ?? "")) updateData.description = description;
      if (startDate !== originalStartDate) updateData.startDate = startDate;
      if (endDate !== originalEndDate) updateData.endDate = endDate;
      if (takeQuantity !== (campaign?.takeQuantity ?? 2)) updateData.takeQuantity = takeQuantity;
      if (payQuantity !== (campaign?.payQuantity ?? 1)) updateData.payQuantity = payQuantity;
      if (discountValue !== (campaign?.discountValue ?? 0)) updateData.discountValue = discountValue;
      if (discountType !== (campaign?.discountType ?? 0)) updateData.discountType = discountType;
      if (baseValue !== (campaign?.baseValue ?? 0)) updateData.baseValue = baseValue;

      // Nada mudou — sai sem chamar API
      const changedFields = Object.keys(updateData).filter((k) => k !== "code");
      if (changedFields.length === 0) {
        toast(NO_CHANGES_TOAST);
        return;
      }

      await updateCampaign(updateData).unwrap();

      toast(MESSAGES.UPDATE.SUCCESS);

      handleModal({ isOpen: false, element: null, title: "", description: "" });
    } catch (err: unknown) {
      const typedErr = err as { status?: number; data?: { message?: string; messages?: string[]; conflicts?: CampaignConflictItem[] } };

      if (typedErr?.status === 409 && typedErr?.data?.conflicts) {
        setConflicts(typedErr.data.conflicts);
        setShowConflictModal(true);
        toast(MESSAGES.CONFLICT.ERROR);
        return;
      }

      const errorMessage = getTranslatedErrorMessage(
        typedErr,
        MESSAGES.UPDATE.ERROR.description,
      );

      toast({ ...MESSAGES.UPDATE.ERROR, description: errorMessage });
    }
  };

  const handleCancel = () => {
    handleModal({ isOpen: false, element: null, title: "", description: "" });
  };

  const handleCloseConflictModal = () => {
    setShowConflictModal(false);
    setConflicts([]);
  };

  // Status flags — usados pra decidir quais campos ficam disabled.
  // Backend só permite editar description/endDate/discountValue em ACTIVE
  // (e tudo em FUTURE; nada em EXPIRED). Sincronizamos a UI com isso.
  const visualStatus = (campaign?.visualStatus ?? campaign?.status ?? "FUTURE") as string;
  const isFuture = visualStatus === "FUTURE";
  const isActive = visualStatus === "ACTIVE";
  const isExpired = visualStatus === "EXPIRED";

  // Loading state
  if (isLoading) {
    return <ModalLoadingState />;
  }

  // Error state
  if (error || !campaign) {
    const loadErrorMessage = getTranslatedErrorMessage(
      error as { status?: number; data?: { message?: string; messages?: string[] } },
      "Erro ao carregar campanha. Tente novamente."
    );
    return <ModalErrorState details={loadErrorMessage} />;
  }

  return (
    <CampaignEditModalLayout
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isUpdating}
      submitDisabled={hasPendingStores}
    >
      {/* Descrição */}
      <div className="space-y-2">
        <Label htmlFor="description">Descrição *</Label>
        <Input
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Descrição da campanha"
          maxLength={PRO014_DESCRIPTION_MAX_LENGTH}
        />
        <p className="text-xs text-muted-foreground">{description.length}/{PRO014_DESCRIPTION_MAX_LENGTH} caracteres</p>
        {errors.description && (
          <p className="text-sm text-destructive">{errors.description}</p>
        )}
      </div>

      {/* Datas — startDate desabilitado em ACTIVE/EXPIRED, endDate sempre editável */}
      <ModalFieldGroup columns={2}>
        <div className="space-y-2">
          <Label htmlFor="startDate">Data de Início *</Label>
          <Input
            id="startDate"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={!isFuture}
            title={!isFuture ? "Data de início não pode ser alterada em campanhas ativas/expiradas" : undefined}
          />
          {!isFuture && (
            <p className="text-xs text-muted-foreground">
              Não é possível alterar a data de início em campanhas {isActive ? "ativas" : "expiradas"}.
            </p>
          )}
          {errors.startDate && (
            <p className="text-sm text-destructive">{errors.startDate}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="endDate">Data de Término *</Label>
          <Input
            id="endDate"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            min={campaign?.startDate ? format(new Date(campaign.startDate), "yyyy-MM-dd") : undefined}
            disabled={isExpired}
          />
          {errors.endDate && (
            <p className="text-sm text-destructive">{errors.endDate}</p>
          )}
        </div>
      </ModalFieldGroup>

      {/* Tipo de Desconto */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4" />
          <Label>Tipo de Desconto *</Label>
        </div>
        <Select
          value={discountType.toString()}
          onValueChange={(value) => setDiscountType(parseInt(value))}
          disabled={!isFuture}
        >
          <SelectTrigger>
            <SelectValue placeholder="Selecione o tipo de desconto" />
          </SelectTrigger>
          <SelectContent>
            {discountTypes.map((type, index, array) => {
              const prevGroup = index > 0 ? array[index - 1].group : null;
              const showGroupHeader = prevGroup !== type.group;

              return (
                <div key={type.value}>
                  {showGroupHeader && (
                    <div className="px-2 py-1 text-xs font-semibold text-muted-foreground bg-muted">
                      {type.group}
                    </div>
                  )}
                  <SelectItem value={type.value.toString()}>{type.label}</SelectItem>
                </div>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {/* Leve e Pague */}
      <ModalFieldGroup columns={2}>
        <div className="space-y-2">
          <Label htmlFor="takeQuantity">Leve *</Label>
          <Input
            id="takeQuantity"
            type="number"
            min="1"
            max="99"
            value={takeQuantity}
            onChange={(e) => setTakeQuantity(parseInt(e.target.value) || 1)}
            disabled={!isFuture}
          />
          {errors.takeQuantity && (
            <p className="text-sm text-destructive">{errors.takeQuantity}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="payQuantity">Pague</Label>
          <Input
            id="payQuantity"
            type="number"
            min="0"
            max="99"
            value={payQuantity}
            onChange={(e) => setPayQuantity(parseInt(e.target.value) || 0)}
            disabled={!isFuture}
          />
          {errors.payQuantity && (
            <p className="text-sm text-destructive">{errors.payQuantity}</p>
          )}
        </div>
      </ModalFieldGroup>

      {/* Valor/Percentual */}
      <div className="space-y-2">
        <Label htmlFor="discountValue">Valor/Percentual do Desconto *</Label>
        <Input
          id="discountValue"
          type="number"
          step="0.01"
          min="0"
          value={discountValue}
          onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
        />
        <p className="text-xs text-muted-foreground">
          Para tipo percentual, insira o valor sem o símbolo % (ex: 50 para 50%)
        </p>
        {errors.discountValue && (
          <p className="text-sm text-destructive">{errors.discountValue}</p>
        )}
      </div>

      {/* Valor Base */}
      <div className="space-y-2">
        <Label htmlFor="baseValue">Valor Base Mínimo (Opcional)</Label>
        <Input
          id="baseValue"
          type="number"
          step="0.01"
          min="0"
          value={baseValue}
          onChange={(e) => setBaseValue(parseFloat(e.target.value) || 0)}
          disabled={!isFuture}
        />
        <p className="text-xs text-muted-foreground">
          Valor mínimo de compra para ativar a promoção
        </p>
      </div>

      {/* Lojas — edição do conjunto com salvamento próprio (PATCH /stores) */}
      <Pro014StoresEditor
        campaignCode={campaignCode}
        onPendingChange={setHasPendingStores}
        renderFooter={(pending: boolean) =>
          pending && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              {PRO014_STORES_TEXTS.SAVE_STORES_FIRST}
            </p>
          )
        }
      />

      <CampaignConflictModal
        isOpen={showConflictModal}
        conflicts={conflicts}
        onClose={handleCloseConflictModal}
        onGoBack={handleCloseConflictModal}
      />
    </CampaignEditModalLayout>
  );
}
